// src/components/ui/dashboard/recommended-career-paths.tsx
"use client"

import {
  ChefHat,
  Stethoscope,
  Atom,
  Palette,
  Code,
  Building2
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useThemeStore } from "@/hooks/stores/themeStore"

export function RecommendedCareerPaths() {
  const navigate = useNavigate()
  const currentTheme = useThemeStore((state) => state.getCurrentTheme());

  const careerPaths = [
    {
      icon: ChefHat,
      title: "Chef",
      bio: "Create culinary masterpieces and manage kitchen operations in restaurants, hotels, or catering services.",
      match: 85,
      education: "Culinary School or Apprenticeship",
      skills: ["Creativity", "Time Management", "Team Leadership"]
    },
    {
      icon: Stethoscope,
      title: "Nurse",
      bio: "Provide patient care, administer treatments, and support doctors in hospitals, clinics, and healthcare facilities.",
      match: 78,
      education: "BSc. of Science in Nursing",
      skills: ["Empathy", "Attention to Detail", "Medical Knowledge"]
    },
    {
      icon: Atom,
      title: "Physicist",
      bio: "Research physical phenomena, develop theories, and apply principles in technology, research, and education.",
      match: 92,
      education: "PhD in Physics",
      skills: ["Analytical Thinking", "Mathematics", "Research Skills"]
    },
    {
      icon: Palette,
      title: "Graphic Designer",
      bio: "Create visual concepts to communicate ideas that inspire, inform, and captivate consumers.",
      match: 73,
      education: "BSc. in Graphic Design",
      skills: ["Creativity", "Software Proficiency", "Visual Communication"]
    },
    {
      icon: Code,
      title: "Software Developer",
      bio: "Design, develop, and maintain software applications and systems for various platforms and industries.",
      match: 88,
      education: "BSc.'s in Computer Science",
      skills: ["Programming", "Problem Solving", "Team Collaboration"]
    },
    {
      icon: Building2,
      title: "Architect",
      bio: "Plan and design buildings, oversee construction projects, and ensure structural integrity and aesthetic appeal.",
      match: 67,
      education: "BSc.'s in Architecture",
      skills: ["Design Skills", "Technical Drawing", "Project Management"]
    }
  ]

  const handleExploreCareer = (careerTitle: string) => {
    navigate(`/career-learning-path?career=${encodeURIComponent(careerTitle)}`)
  }

  return (
    <div
      className="p-6 rounded-2xl shadow-lg"
      style={{
        backgroundColor: 'var(--theme-card)',
        border: `1px solid ${currentTheme.colors.border}`
      }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h3
            className="text-2xl font-bold"
            style={{ color: 'var(--theme-foreground)' }}
          >
            Recommended Career Paths
          </h3>
          <p
            className="mt-1"
            style={{ color: 'var(--theme-foreground)', opacity: 0.7 }}
          >
            Based on your interests and assessment results
          </p>
        </div>
      </div>

      {/* 3x2 Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {careerPaths.map((career, index) => (
          <div
            key={index}
            className="p-5 rounded-xl border transition-all duration-200 flex flex-col hover:shadow-lg"
            style={{
              backgroundColor: currentTheme.colors.muted,
              border: `1px solid ${currentTheme.colors.border}`,
              borderLeft: `4px solid ${currentTheme.colors.primary}`
            }}
          >
            {/* Career Icon & Title */}
            <div className="flex items-start gap-4 mb-4">
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: `${currentTheme.colors.primary}15`
                }}
              >
                <career.icon
                  className="w-6 h-6"
                  style={{ color: currentTheme.colors.primary }}
                />
              </div>
              <div className="flex-1">
                <h4
                  className="font-bold text-lg"
                  style={{ color: 'var(--theme-foreground)' }}
                >
                  {career.title}
                </h4>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-1">
                    <div
                      className="w-24 h-2 rounded-full overflow-hidden"
                      style={{ backgroundColor: currentTheme.colors.border }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{
                          backgroundColor: currentTheme.colors.primary,
                          width: `${career.match}%`
                        }}
                      />
                    </div>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: currentTheme.colors.primary }}
                    >
                      {career.match}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Career Bio */}
            <p
              className="text-sm mb-4 flex-grow"
              style={{ color: 'var(--theme-foreground)', opacity: 0.7 }}
            >
              {career.bio}
            </p>

            {/* Explore Button */}
            <button
              onClick={() => handleExploreCareer(career.title)}
              className="mt-auto w-full font-medium py-3 rounded-lg transition-all duration-200 hover:scale-[1.02]"
              style={{
                backgroundColor: currentTheme.colors.primary,
                color: 'white'
              }}
            >
              Explore Path
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}